import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { SocialShare } from "@quintype/components";
import throttle from "lodash/throttle";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";

import ThreeColGrid from "../../../arrow/components/Rows/ThreeColGrid";
import { SocialShareTemplate } from "../../../arrow";
import { getAdvancedSearchItems } from "../../../../api/utils";
import { STATIC_KEYWORDS } from "../../../constants";
import { handleBagdes } from "./utils";
import { STORY_HEADER_IN_VIEW } from "../../store/actions";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { UserFallbackIcon } from "../../../arrow/components/Svgs/user-fallback-icon";
import { useScrollToElement } from "../../customHooks/useScrollToElement";

import "./individual-page.m.css";

const IndividualPage = ({ data = [], config }) => {
  const cdnImage = `https://${config?.["cdn-image"]}`;
  const featuredRef = useRef(null);
  const scrollToFeatured = useScrollToElement(featuredRef);
  const { individualInfo, tagResponse, slug } = data;

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";

  const {
    name,
    "display-name": displayName,
    "40u40ranks": fortyUFortyRanks,
    mpwranks,
    designation,
    bio,
    dob,
    photo,
    company,
  } = individualInfo;
  const nameShown = displayName || name;
  const dispatch = useDispatch();
  const allStates = useSelector((state) => state);
  const storyHeaderInView = get(allStates, "storyHeaderInView", true);

  const fortyBadgeData = fortyUFortyRanks ? JSON.parse(fortyUFortyRanks) : [];
  const fortyBadgeRanks = fortyBadgeData.map((item) => {
    const [yearKey, rankValue] = Object.entries(item)[0];
    return { type: "40 Under 40 ", year: Number(yearKey), rank: Number(rankValue) };
  });
  const mpwBadgeData = mpwranks ? JSON.parse(mpwranks) : [];
  const mpwBadgeRanks = mpwBadgeData.map((item) => {
    const [yearKey, rankValue] = Object.entries(item)[0];
    return { type: "Most Powerful Women ", year: Number(yearKey), rank: Number(rankValue) };
  });
  const badgeData = mpwBadgeRanks.concat(fortyBadgeRanks).sort((a, b) => b.year - a.year);
  const [entityStories, setEntityStories] = useState(tagResponse.items);
  const { ABOUT_PROFILE, PROFILE_ORGANIZATION, PROFILE_AGE, FEATURED_IN } = STATIC_KEYWORDS;
  const [shareURL, setShareURL] = useState("");

  // Track people detail interactions
  let isTracking = false;
  const trackPeopleDetailInteraction = (action, value) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_people_detail_interactions",
        event_action: action,
        link_text: value?.toLowerCase(),
        category: "people",
        sub_category: nameShown?.toLowerCase() || "NA",
        template_name: "profilepage",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  useEffect(() => {
    setShareURL(window.location.href);
  }, []);

  const handleRankings = (rankingString, ranking, header) => {
    if (!ranking.length) return;
    // Track ranking list clicks
    const handleRankingClick = () => {
      trackPeopleDetailInteraction("ranking_list_click", header);
    };

    const rankingSorted = ranking.sort((a, b) => {
      const yearA = parseInt(Object.keys(a)[0]);
      const yearB = parseInt(Object.keys(b)[0]);
      return yearB - yearA;
    });

    const rankingsList = () => {
      return rankingSorted.map((item, index) => {
        const parseRank = parseInt(Object.values(item)[0]);

        const prependZero = parseRank < 10 ? `#0${parseRank}` : `#${parseRank}`;
        return (
          <div key={`${Object.keys(item)[0]}-${index}`} styleName="ranks">
            {rankingString === "mpw" && <span styleName="rank">{prependZero} </span>}
            <span styleName="year">{`${rankingString === "mpw" ? "(" : ""}${Object.keys(item)[0]}${
              rankingString === "mpw" ? ")" : ""
            }`}</span>
            {index !== rankingSorted.length - 1 && <span styleName="line" />}
          </div>
        );
      });
    };

    return (
      <div styleName={`fortune-header`} onClick={handleRankingClick}>
        <div styleName="logo-container">
          <div styleName={`fortune-logo ${rankingString === "mpw" ? "mpw-logo" : ""}`}>
            {rankingString === "mpw" ? (
              <a href="/rankings/most-powerful-women">
                <img src="/images/logos-square/mpw-logo.png" width={88} height={88} alt={header} title={header} loading="lazy" />
              </a>
            ) : (
              <a href="/rankings/40under40">
                <img src="/images/logos-square/40-under-40.png" width="88" height="88" alt="40 under 40" title="40 under 40" loading="lazy" />
              </a>
            )}
          </div>
          <a
            styleName="ranking-name mobile"
            href={rankingString === "mpw" ? "/rankings/most-powerful-women" : "/rankings/40under40"}
          >
            {header}
          </a>
        </div>
        <div>
          <a
            href={rankingString === "mpw" ? "/rankings/most-powerful-women" : "/rankings/40under40"}
            styleName="ranking-name desktop-only"
          >
            {header}
          </a>
          <div styleName="ranking-container">{rankingsList()}</div>
        </div>
      </div>
    );
  };

  const getMoreStories = async () => {
    const splitTagSlug = slug.split("-").join(" ");
    const tagResults = await getAdvancedSearchItems("tag-name-lower", splitTagSlug, entityStories.length, 5);
    const getStories = tagResults.map((item) => item?.story);
    setEntityStories(entityStories.concat(getStories));
  };
  const peopleHeader = useRef(null);
  const handleScroll = () => {
    const peopleHeaderPosition = peopleHeader.current.getBoundingClientRect();
    if (peopleHeaderPosition.bottom < 75 && storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (peopleHeaderPosition.bottom > 75 && !storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "people",
      sub_category: nameShown || "NA",
      content_id: "NA",
      template_name: "profilepage",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  return (
    <div styleName="parent-container">
      <div styleName={`profile-top ${isAdfreePage ? "no-ad-profile-top" : ""}`} ref={peopleHeader}>
        <div styleName="info-container">
          <div styleName="name-logo-container">
            {photo?.[0]?.key ? (
              <figure styleName="profile-image">
                <img width={130} height={130} loading="lazy" alt={nameShown} title={nameShown} src={`${cdnImage}/${photo?.[0]?.key}?w=126&q=70`} />
              </figure>
            ) : (
              <UserFallbackIcon />
            )}
            <div styleName="main-info">
              <div styleName="primary-details">
                <h1 styleName="name">{nameShown}</h1>
                <span styleName="designation-container">
                  {designation && <span styleName="designation">{designation}</span>}
                  {company && <span styleName="designation">, {company}</span>}
                </span>
                {handleBagdes(badgeData, scrollToFeatured, (badgeName) =>
                  trackPeopleDetailInteraction("badge_clicks", badgeName)
                )}
              </div>
              <div styleName="share">
                <span className="content-style">
                  <SocialShare
                    popupText="Share this URL"
                    template={SocialShareTemplate}
                    onShareClick={(platform) =>
                      trackPeopleDetailInteraction("share_click", platform?.toLowerCase() || "share")
                    }
                    fullUrl={encodeURI(shareURL)}
                    title={`${nameShown} on Fortune India`}
                    shareIconId={`share-icon-company-share`}
                    shareText={true}
                  />
                </span>
              </div>
            </div>
          </div>
          {!isAdfreePage && (
            <div styleName="mobile-ad">
              <ListingAdSlot
                adType="right_side"
                pageType="individual-page"
                adStyleName="ad-slot-size-300x250"
                index={1}
                adSlotConfigName={"mRec_ad"}
                showText={false}
              />
            </div>
          )}
        </div>
        {!isAdfreePage && (
          <div styleName="ad">
            <ListingAdSlot
              adType="right_side"
              pageType="individual-page"
              adStyleName="ad-style-300x250"
              index={2}
              adSlotConfigName={"mRec_ad"}
              showText={false}
            />
          </div>
        )}
      </div>

      <div styleName="profile-overview">
        {(bio || company || dob) && (
          <section styleName="about-section">
            <h2>{ABOUT_PROFILE}</h2>
            {bio && <p>{bio}</p>}
            <div styleName="profile-info-grid">
              {company && (
                <div styleName="info-column">
                  <span styleName="label">{PROFILE_ORGANIZATION}</span> <span styleName="value">{company}</span>
                </div>
              )}
              {dob && (
                <div styleName="info-column">
                  <span styleName="label">{PROFILE_AGE}</span> <span styleName="value">{dob}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
      {(mpwBadgeData?.length > 0 || fortyBadgeData?.length > 0) && (<section ref={featuredRef} styleName="fortune-section">
        <h2>{FEATURED_IN}</h2>
        <div styleName="historical-ranks">
          {handleRankings("mpw", mpwBadgeData, "Most Powerful Women")}
          {handleRankings("40Under40", fortyBadgeData, "40 Under 40")}
        </div>
      </section>)}
      {tagResponse.total > 0 && (
        <section styleName="three-coll-grid small-loadmore">
          <h3 styleName="title">Fortune India Coverage</h3>
          <ThreeColGrid
            collection={entityStories}
            config={{
              buttonText: "Load More",
              footerButton: "SubsequentLoadCount",
              showAuthor: true,
              showTime: true,
              showReadTime: false,
            }}
            isLoadMoreVisible={tagResponse.total > entityStories.length}
            getMoreStories={getMoreStories}
            initialLoadCount={6}
            isolatedLoadMore={true}
            page="profile-page"
            showAuthorContent={false}
          />
        </section>
      )}
    </div>
  );
};

IndividualPage.propTypes = {
  data: PropTypes.array,
};

export { IndividualPage };
